<script lang="ts" setup>
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import TextCheckOptions from '@/components/Jobs/CopilotActivation/TextCheckOptions.vue';
import { computed, ref, watch } from 'vue';
// import { EmployerService } from '@/core/shared/employer/employer.service';
import ScheduledUpdatesService from '@/core/jobs/scheduled-updates/scheduled-updates.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import ScheduledUpdatesModal from '@/components/Jobs/CopilotActivation/ScheduledUpdatesModal.vue';
import ExclamationIcon from '@/assets/svg/exclamation-circle-16.svg?component';
import LightningCircleIcon from '@/assets/svg/lightning-circle-24.svg?component';
import { PolicyEnforcement, ProjectDistributionStatusType } from '@factoryfixinc/ats-interfaces';
import PolicyTooltip from './PolicyTooltip.vue';

const copilotActivationService = new CopilotActivationService();
// const employerService = new EmployerService();
const scheduledUpdatesService = new ScheduledUpdatesService();

const props = defineProps<{
  isDisabled: boolean;
  projectId?: number;
  copilotStatus: string;
  scheduledUpdatesPolicy?: PolicyEnforcement;
}>();

const isScheduledUpdatesModalOpen = ref(false);
const showTooltip = ref(false);

const isScheduledUpdatesActive = computed<boolean>(
  () => copilotActivationService.scheduledUpdates === true,
);

const isRequiredPolicy = computed(
  () => props.scheduledUpdatesPolicy === PolicyEnforcement.REQUIRED,
);
const isForbiddenPolicy = computed(
  () => props.scheduledUpdatesPolicy === PolicyEnforcement.FORBIDDEN,
);

const toggleScheduledUpdates = (selected: boolean) => {
  if (selected && isForbiddenPolicy.value) {
    return;
  }

  if (!selected && isRequiredPolicy.value) {
    return;
  }

  copilotActivationService.scheduledUpdates = selected;
};

// TODO: Implement scheduled updates feature check using employer policies
const hasScheduledUpdatesFeature = computed<boolean>(() => true);

const hasScheduledUpdates = computed<boolean>(
  () => scheduledUpdatesService.currentScheduledUpdate !== null,
);

const isGeneratingScheduledUpdates = computed<boolean>(
  () =>
    hasScheduledUpdates.value &&
    scheduledUpdatesService.currentScheduledUpdate?.status ===
      ProjectDistributionStatusType.GENERATING,
);

const currentJobId = computed<number>(() => copilotActivationService.selectedJobId ?? 0);

watch(currentJobId, async (newJobId: number, previousJobId: number) => {
  if (newJobId !== 0 && newJobId !== previousJobId) {
    try {
      await scheduledUpdatesService.loadScheduledUpdateByJobId(newJobId);
    } catch (error) {
      scheduledUpdatesService.currentScheduledUpdate = null;
      ErrorService.captureException(error);
    }
  }
});
</script>

<template>
  <div
    v-if="hasScheduledUpdatesFeature"
    id="scheduled-updates"
    class="mt-6"
    :class="{ 'pointer-events-none opacity-75': isDisabled }"
  >
    <div class="scheduled-updates-title">
      How would you like your AI recruiter to manage this job's visibility on job boards?
    </div>
    <div class="flex flex-row gap-3">
      <TextCheckOptions
        :is-selected="isScheduledUpdatesActive && !isForbiddenPolicy"
        :is-disabled="isForbiddenPolicy"
        @click="toggleScheduledUpdates(true)"
        ><template v-slot:title>
          <span class="flex items-center justify-between">
            Optimize visibility
            <PolicyTooltip
              v-if="isForbiddenPolicy"
              :feature-policy="scheduledUpdatesPolicy"
              feature="Optimize visibility"
            />
          </span>
        </template>
        Your AI recruiter will adapt the title and description periodically to maintain high
        rankings and steady applicant flow
        <template v-slot:action>
          <template v-if="hasScheduledUpdates">
            <div
              v-if="isGeneratingScheduledUpdates"
              class="mt-0.5 flex items-center text-sm text-shade-800"
            >
              <LightningCircleIcon class="mr-0.5 scale-75" />
              <span class="breath-effect">Generating updates...</span>
            </div>
            <div
              v-else
              @click.prevent.stop="isScheduledUpdatesModalOpen = true"
              class="mt-0.5 cursor-pointer text-sm font-bold text-highlight-700 hover:opacity-70"
            >
              See scheduled changes
            </div>
          </template>
          <div v-else class="mt-0.5 flex items-center text-sm text-shade-800">
            <v-tooltip
              v-model="showTooltip"
              location="bottom"
              content-class="project-card-tooltip tooltip-top-arrow"
            >
              <template #activator="{ props }">
                <div v-bind="props">
                  <!-- Figma design shows an "i" for Information thats why I am rotating the
                   Exclamation icon we already have -->
                  <ExclamationIcon class="mr-0.5 rotate-180" />
                </div>
              </template>
              <span class="text-xs">
                Scheduled updates will be generated<br />
                once Copilot is activated
              </span>
            </v-tooltip>
            <!-- Tooltip design in Figma show it centered over the icon: this will allow
             to show when hovering on the text too -->
            <span @mouseover="showTooltip = true" @mouseleave="showTooltip = false"
              >See scheduled changes</span
            >
          </div>
        </template>
      </TextCheckOptions>
      <TextCheckOptions
        :is-selected="!isScheduledUpdatesActive && !isRequiredPolicy"
        :is-disabled="isRequiredPolicy"
        @click="toggleScheduledUpdates(false)"
        ><template v-slot:title>
          <span class="flex items-center justify-between">
            Keep original
            <PolicyTooltip
              v-if="isRequiredPolicy"
              :feature-policy="scheduledUpdatesPolicy"
              feature="Keep original"
            />
          </span>
        </template>
        Always use your exact job details; rankings will decline over time, reducing inbound
        applicant traffic
      </TextCheckOptions>
    </div>
  </div>
  <ScheduledUpdatesModal
    :model-value="isScheduledUpdatesModalOpen"
    :project-id="projectId"
    :copilot-status="copilotStatus"
    @exit="isScheduledUpdatesModalOpen = false"
  ></ScheduledUpdatesModal>
</template>
<style lang="postcss" scoped>
.scheduled-updates-title {
  @apply mb-4 text-lg font-extrabold;
}
@keyframes breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.breath-effect {
  animation: breathe 1.5s ease-in-out infinite;
}
</style>
